import { MergePreserveOptional } from 'helpers/types';

export type TreeWithIsDisabled = MergePreserveOptional<
    TreeT,
    {
        isDisabled: boolean;
        children: TreeWithIsDisabled[];
    }
>;

export const enrichTreeWithTrueIsDisabledField = (
    tree: TreeT,
): TreeWithIsDisabled => {
    const children = tree.children.map(enrichTreeWithTrueIsDisabledField);
    return { ...tree, key: tree.key, children, isDisabled: true };
};

export const getTreeWithIsDisabledField = (
    tree: TreeT,
    selection: number[],
): TreeWithIsDisabled => {
    if (selection.includes(tree.id))
        return {
            ...enrichTreeWithTrueIsDisabledField(tree),
            isDisabled: false,
        };

    const newChildren: TreeWithIsDisabled[] = tree.children.map((child) =>
        getTreeWithIsDisabledField(child, selection),
    );
    return { ...tree, key: tree.key, isDisabled: false, children: newChildren };
};

/**
 * Takes a tree and return a Map:
 * - key : node id
 * - value : all its ancestors
 * @example
 * Input: tree = {
 *  id: 0
 *  children : [
 *      { id:1, children : [ { id:11 } ] },
 *      { id:2, children : [
 *          { id:21 }
 *          { id:22, children:[ { id : 221 } ] }
 *       ]},
 * ]}
 * Output: {
 *          0:   [],
 *          1:   [0],
 *          11:  [1, 0],
 *          2:   [0],
 *          21:  [2, 0],
 *          22:  [2, 0],
 *          221: [22, 2, 0],
 * }
 */
export const getNodeIdAndAncestorsIdsMap = (
    tree: TreeT,
): { [id: number]: number[] } => {
    const map: { [id: string]: number[] } = { [tree.id]: [] };

    tree.children.forEach((child) => {
        const childMap = getNodeIdAndAncestorsIdsMap(child);
        Object.entries(childMap).forEach(([id, idMap]: [string, number[]]) => {
            map[id] = [...idMap, tree.id];
        });
    });

    return map;
};
